import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../UserContext';
import { useNavigate } from 'react-router-dom';
import { servicesViewModel } from '../types/ServiceEnum';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import FormActionButton from '../components/FormActionButton';
import Container from '../components/Container';
import Heading2 from '../components/Heading2';

interface ServiceSelectorProps {
  services: typeof servicesViewModel;
  selected: string[];
  onSelected: (_: string[]) => void;
}

const ServiceSelector: React.FC<ServiceSelectorProps> = ({ services, selected, onSelected }) => {
  return (
    <fieldset className="mb-6 mx-auto w-full">
      <RadioGroup
        value={selected}
        onChange={onSelected}
        className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-1 sm:gap-x-4"
      >
        {Object.entries(services).map(([k, v]) => (
          <RadioGroup.Option
            key={k}
            value={v.serviceIds}
            aria-label={``}
            className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-sm focus:outline-none data-[focus]:border-deep-blue data-[focus]:ring-2 data-[focus]:ring-deep-blue"
          >
            <div className="flex flex-1">
              <div className="flex flex-col">
                <span className="text-gray-700 text-lg">{v.label}</span>
                <span className="block text-sm text-gray-500">{v.description}</span>
              </div>
            </div>
            <CheckCircleIcon
              aria-hidden="true"
              className="h-5 w-5 text-deep-blue [.group:not([data-checked])_&]:invisible"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-deep-blue"
            />
          </RadioGroup.Option>
        ))}
      </RadioGroup>
    </fieldset>
  );
};

const ChooseServiceScreen: React.FC = () => {
  const userContext = useContext(UserContext);
  const [serviceIds, setServiceIds] = useState<string[]>(userContext?.userData.service_ids || []);
  const navigate = useNavigate();

  useEffect(() => {
    if (userContext) {
      const { service_ids } = userContext.userData;
      setServiceIds(service_ids || []);
    }
  }, [userContext]);

  if (!userContext) return null;

  const { userData, setUserData } = userContext;

  const handleNext = async () => {
    setUserData({ ...userData, service_ids: serviceIds });
    navigate('/choose-day');
  };

  return (
    <Container>
      <Heading2>Select a Treatment</Heading2>
      <p className="text-gray-700 mb-6">
        Looking to book an appointment at a Tox event with a specific Skin Clique provider? You can{' '}
        <a href="https://book.skinclique.com/webstoreNew/services" className="font-bold underline">
          search for your provider here
        </a>
        .
      </p>
      <ServiceSelector services={servicesViewModel} selected={serviceIds} onSelected={setServiceIds} />
      <div className="flex justify-center mt-4">
        <FormActionButton onClick={handleNext} isLoading={false}>
          Next
        </FormActionButton>
      </div>
    </Container>
  );
};

export default ChooseServiceScreen;
